import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import apiClient from './apiClient';

const roleKeys = {
  all: ['roles'],
  lists: () => [...roleKeys.all, 'list'],
  details: () => [...roleKeys.all, 'detail'],
  detail: (id) => [...roleKeys.details(), id],
  modules: () => [...roleKeys.all, 'modules'],
  roleModule: (roleId, moduleId) => [...roleKeys.modules(), roleId, moduleId],
};

/**
 * Hook para obtener todos los roles
 */
export const useRoles = () => {
  return useQuery({
    queryKey: roleKeys.lists(),
    queryFn: async () => {
      const response = await apiClient.get('roles');
      return response.data;
    },
    staleTime: 10 * 60 * 1000,
    gcTime: 15 * 60 * 1000,
  });
};

/**
 * Hook para obtener un rol específico
 * @param {string} id - UUID del rol
 * @param {Object} options - Opciones adicionales para la query
 */
export const useRole = (id, options = {}) => {
  return useQuery({
    queryKey: roleKeys.detail(id),
    queryFn: async () => {
      const response = await apiClient.get(`roles/${id}`);
      return response.data;
    },
    enabled: !!id,
    staleTime: 5 * 60 * 1000,
    ...options,
  });
};

/**
 * Hook para crear un nuevo rol
 */
export const useCreateRole = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (roleData) => {
      const response = await apiClient.post('roles', roleData);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: roleKeys.lists() });
    },
    onError: (error) => {
      console.error('Error creating role:', error);
    },
  });
};

/**
 * Hook para actualizar un rol existente
 */
export const useUpdateRole = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (roleData) => {
      const response = await apiClient.put(`roles/${roleData.uuid}`, roleData);
      return response.data;
    },
    onSuccess: (data) => {
      queryClient.setQueryData(roleKeys.detail(data.uuid), data);
      queryClient.invalidateQueries({ queryKey: roleKeys.lists() });
    },
    onError: (error) => {
      console.error('Error updating role:', error);
    },
  });
};

/**
 * Hook para eliminar un rol
 */
export const useDeleteRole = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id) => {
      const response = await apiClient.delete(`roles/${id}`);
      return response.data;
    },
    onSuccess: (data, deletedId) => {
      queryClient.invalidateQueries({ queryKey: roleKeys.lists() });
      queryClient.removeQueries({ queryKey: roleKeys.detail(deletedId) });
    },
    onError: (error) => {
      console.error('Error deleting role:', error);
    },
  });
};

/**
 * Hook para agregar un módulo a un rol
 */
export const useAddRoleModule = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (roleModule) => {
      const { uuid: roleId, name: moduleId } = roleModule;
      const response = await apiClient.post(`rol_module/${roleId}/${moduleId}`);
      return response.data;
    },
    onSuccess: (data, variables) => {
      const { uuid: roleId } = variables;
      queryClient.invalidateQueries({ queryKey: roleKeys.detail(roleId) });
      queryClient.invalidateQueries({ queryKey: roleKeys.lists() });
    },
    onError: (error) => {
      console.error('Error adding role module:', error);
    },
  });
};

/**
 * Hook para eliminar un módulo de un rol
 */
export const useDeleteRoleModule = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (roleModule) => {
      const { uuid: roleId, name: moduleId } = roleModule;
      const response = await apiClient.delete(`rol_module/${roleId}/${moduleId}`);
      return response.data;
    },
    onSuccess: (data, variables) => {
      const { uuid: roleId } = variables;
      queryClient.invalidateQueries({ queryKey: roleKeys.detail(roleId) });
      queryClient.invalidateQueries({ queryKey: roleKeys.lists() });
    },
    onError: (error) => {
      console.error('Error deleting role module:', error);
    },
  });
};

/**
 * Hook para actualizar módulos de un rol en batch
 */
export const useBatchUpdateRoleModules = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ roleId, moduleUuids }) => {
      const response = await apiClient.put('roles_modules/batch-update', {
        uuid_rol: roleId,
        module_uuids: moduleUuids,
      });
      return response.data;
    },
    onSuccess: (data, variables) => {
      const { roleId } = variables;
      queryClient.invalidateQueries({ queryKey: roleKeys.detail(roleId) });
      queryClient.invalidateQueries({ queryKey: roleKeys.lists() });
    },
    onError: (error) => {
      console.error('Error batch updating role modules:', error);
    },
  });
};
